import * as XLSX from 'xlsx';
import {
  extractMetadataOptions,
  findLookupById,
  findLookupByName,
  parseCsvText,
} from '../assigner-bulk-upload/assigner-bulk-upload.util';
import {
  createDefaultSuperAdminBulkSelections,
  SUPER_ADMIN_RECIPE_STATUS_OPTIONS,
  SUPER_ADMIN_RECIPE_TEMPLATE_HEADERS,
  SUPER_ADMIN_SHELF_LIFE_UNITS,
  SUPER_ADMIN_TEMPERATURE_UNITS,
  SuperAdminBulkIngredientDraft,
  SuperAdminBulkLookups,
  SuperAdminBulkParseResult,
  SuperAdminBulkParsedText,
  SuperAdminBulkPreviewRow,
  SuperAdminBulkRecipeIngredient,
  SuperAdminBulkRowSelections,
  SuperAdminBulkGeneratedFssaiTag,
} from './super-admin-recipe-bulk-upload.models';

function normalizeHeader(header: string): string {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function cell(row: Record<string, string>, ...aliases: string[]): string {
  for (const alias of aliases) {
    const key = normalizeHeader(alias);
    const match = Object.keys(row).find((k) => normalizeHeader(k) === key);
    if (match != null && row[match] != null) {
      return String(row[match]).trim();
    }
  }
  return '';
}

export type SuperAdminRecipeTemplateFormat = 'csv' | 'xlsx';

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Download blank RDMS recipe bulk-upload template as CSV or Excel (.xlsx). */
export function downloadSuperAdminRecipeTemplate(
  format: SuperAdminRecipeTemplateFormat = 'csv'
): void {
  const headers = [...SUPER_ADMIN_RECIPE_TEMPLATE_HEADERS];
  const baseName = 'SuperAdmin_RDMS_Recipe_Template';

  if (format === 'xlsx') {
    const sheet = XLSX.utils.aoa_to_sheet([headers]);
    // Widen columns slightly for readability in Excel
    sheet['!cols'] = headers.map((h) => ({ wch: Math.min(36, Math.max(14, h.length + 2)) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Recipes');

    const guide = XLSX.utils.aoa_to_sheet([
      ['RDMS Recipe Bulk Upload — Template Guide'],
      [''],
      ['Required columns', 'None — all columns optional. Empty Name imports as Untitled Recipe (Row N).'],
      ['Master match', 'Fill name columns and/or * Ids. Values that exist in master auto-fill Configure Row tabs.'],
      ['Multi values', 'Separate multiple names/IDs with comma, semicolon, or pipe (e.g. Taste: Sweet,Sour).'],
      ['Ingredients', 'Example: Cluster beans 100gm | Peanut 15gm | Oil 1tsp'],
      ['Professional Notes', 'Title|Body;;Title|Body'],
      ['Chef Method Processes', 'ProcessName: step1|step2;;ProcessName: step1|step2'],
      ['Status', 'Draft (default). Unknown statuses (e.g. Research Completed) import as Draft.'],
          ['Formats', 'Upload this .xlsx or .csv — full 237-field exports are also accepted.'],
          ['Master values', 'Any master column accepts ID or name (or mix): e.g. "12" or "Breakfast" or "12,Lunch".'],
        ]);
    guide['!cols'] = [{ wch: 28 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(book, guide, 'Guide');

    XLSX.writeFile(book, `${baseName}.xlsx`);
    return;
  }

  // UTF-8 BOM so Excel opens CSV with correct encoding
  const csvContent = '\uFEFF' + headers.map(escapeCsvCell).join(',') + '\n';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  triggerBrowserDownload(blob, `${baseName}.csv`);
}

function escapeCsvCell(value: string): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function parseTimeToMinutes(value: string): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (raw.includes(':')) {
    const [hh, mm] = raw.split(':');
    const hours = Number(hh) || 0;
    const mins = Number(mm) || 0;
    const total = hours * 60 + mins;
    return total > 0 ? total : null;
  }
  const asNum = Number(raw);
  return Number.isFinite(asNum) && asNum > 0 ? asNum : null;
}

export function parseProfessionalNotes(raw: string): Array<{ title: string; body: string }> {
  return String(raw || '')
    .split(';;')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const pipe = part.indexOf('|');
      if (pipe >= 0) {
        return { title: part.slice(0, pipe).trim(), body: part.slice(pipe + 1).trim() };
      }
      return { title: '', body: part };
    })
    .filter((n) => n.title || n.body);
}

export function parseSemicolonList(raw: string): string[] {
  return String(raw || '')
    .split(/[;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseChefMethodProcesses(
  raw: string
): Array<{ name: string; steps: string[] }> {
  const text = String(raw || '').trim();
  if (!text) return [];

  // 237-field export often uses pipe-separated steps without process names.
  if (!text.includes(';;') && text.includes('|') && !text.includes(':')) {
    const steps = text
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    return steps.length ? [{ name: 'Cooking Process', steps }] : [];
  }

  return text
    .split(';;')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const colon = part.indexOf(':');
      if (colon < 0) {
        if (part.includes('|')) {
          return {
            name: 'Cooking Process',
            steps: part
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean),
          };
        }
        return { name: part, steps: [] };
      }
      const name = part.slice(0, colon).trim();
      const steps = part
        .slice(colon + 1)
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean);
      return { name, steps };
    })
    .filter((p) => p.name || p.steps.length);
}

export function parseIngredientsRaw(raw: string): SuperAdminBulkIngredientDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  if (text.includes(';;')) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pieces = part.split('|').map((p) => p.trim());
        const name = pieces[0] || '';
        const quantity = parseQuantityToken(pieces[1]);
        const approximateGrams = parseQuantityToken(pieces[2]) || 0;
        return { name, quantity, approximateGrams };
      })
      .filter((i) => i.name);
  }

  // Formats:
  // - "Cluster beans  100gm | Peanut  15gm"
  // - "Whole wheat flour (gehun ka atta): 1 cup (For dough) | Salt: 0.25 tsp (For dough)"
  return text
    .split('|')
    .map((part) => part.replace(/\t+/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((part) => {
      const colon = part.match(
        /^(.*?):\s*(\d+(?:\/\d+)?(?:\.\d+)?)\s*(cup|cups|tsp|tsps|tbsp|tbsps|teaspoon|tablespoon|g|gm|grams?|kg|ml|nos?|pc|pcs|pinch|cloves?)?\b/i
      );
      if (colon) {
        const quantity = parseQuantityToken(colon[2]);
        const unitLabel = (colon[3] || '').trim() || undefined;
        const gramLike = !unitLabel || /^(g|gm|grams?|kg|ml)$/i.test(unitLabel);
        return {
          name: colon[1].replace(/\s+/g, ' ').trim(),
          quantity,
          approximateGrams: gramLike ? quantity : 0,
          unitLabel,
        };
      }

      const match = part.match(
        /^(.*?)(?:\s+)(\d+(?:\/\d+)?(?:\.\d+)?)\s*([a-zA-Z%µu]+)?\s*$/i
      );
      if (match) {
        const quantity = parseQuantityToken(match[2]);
        const unitLabel = (match[3] || '').trim() || undefined;
        const gramLike = !unitLabel || /^(g|gm|grams?|kg|ml)$/i.test(unitLabel);
        return {
          name: match[1].replace(/\s+/g, ' ').trim(),
          quantity,
          approximateGrams: gramLike ? quantity : 0,
          unitLabel,
        };
      }
      return { name: part.replace(/\s+/g, ' ').trim(), quantity: 0, approximateGrams: 0 };
    })
    .filter((i) => i.name);
}

function parseQuantityToken(raw: string): number {
  const t = String(raw || '').trim();
  if (!t) return 0;
  if (t.includes('/')) {
    const [a, b] = t.split('/');
    const num = Number(a);
    const den = Number(b);
    if (Number.isFinite(num) && Number.isFinite(den) && den !== 0) return num / den;
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

function draftToRecipeIngredient(
  draft: SuperAdminBulkIngredientDraft,
  linked?: SuperAdminBulkRecipeIngredient | null
): SuperAdminBulkRecipeIngredient {
  const unitLabel = draft.unitLabel || linked?.unitLabel || '';
  const quantity = draft.quantity || linked?.quantity || 0;
  const approxGrams = draft.approximateGrams || 0;
  return {
    ingredientId: linked?.ingredientId || 0,
    ingredientName: draft.name,
    servingUnitMasterId: linked?.servingUnitMasterId || 0,
    unitLabel,
    approximateValue: approxGrams || linked?.approximateValue || 0,
    quantity,
    totalGrams: approxGrams || linked?.totalGrams || 0,
    calories: linked?.calories || 0,
    protein: linked?.protein || 0,
    fat: linked?.fat || 0,
    carbs: linked?.carbs || 0,
    fiber: linked?.fiber || 0,
  };
}

function mergeIngredientDrafts(
  a: SuperAdminBulkIngredientDraft[],
  b: SuperAdminBulkIngredientDraft[]
): SuperAdminBulkIngredientDraft[] {
  const map = new Map<string, SuperAdminBulkIngredientDraft>();
  const order: string[] = [];

  const upsert = (draft: SuperAdminBulkIngredientDraft, preferIncoming: boolean) => {
    const key = String(draft.name || '')
      .trim()
      .toLowerCase();
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      order.push(key);
      map.set(key, { ...draft });
      return;
    }
    if (!preferIncoming) return;
    map.set(key, {
      name: draft.name || existing.name,
      quantity: draft.quantity || existing.quantity,
      approximateGrams: draft.approximateGrams || existing.approximateGrams,
      unitLabel: draft.unitLabel || existing.unitLabel,
    });
  };

  // Keep order from the longer list; overlay values from the other.
  const [primary, secondary] = a.length >= b.length ? [a, b] : [b, a];
  primary.forEach((d) => upsert(d, false));
  secondary.forEach((d) => upsert(d, true));
  return order.map((k) => map.get(k)!).filter(Boolean);
}

/** Resolve Ingredients N… columns + Ingredients text blob into drafts and recipe rows. */
export function resolveIngredientsFromFileRow(
  raw: Record<string, string>,
  ingredientsRaw: string
): {
  drafts: SuperAdminBulkIngredientDraft[];
  recipeIngredients: SuperAdminBulkRecipeIngredient[];
} {
  const fromColumns = parseIngredientColumns(raw);
  const fromRaw = parseIngredientsRaw(ingredientsRaw);
  const drafts = mergeIngredientDrafts(fromColumns.drafts, fromRaw);

  const linkedByName = new Map<string, SuperAdminBulkRecipeIngredient>();
  fromColumns.recipeIngredients.forEach((ing) => {
    const key = String(ing.ingredientName || '')
      .trim()
      .toLowerCase();
    if (key) linkedByName.set(key, ing);
  });

  // Always promote file ingredients into the editable recipe list (master IDs optional).
  const recipeIngredients = drafts.map((d) =>
    draftToRecipeIngredient(d, linkedByName.get(d.name.trim().toLowerCase()) || null)
  );

  return { drafts, recipeIngredients };
}

/** Numbered "Ingredients N …" columns from the 237-field Excel export. */
export function parseIngredientColumns(raw: Record<string, string>): {
  drafts: SuperAdminBulkIngredientDraft[];
  recipeIngredients: SuperAdminBulkRecipeIngredient[];
} {
  const drafts: SuperAdminBulkIngredientDraft[] = [];
  const recipeIngredients: SuperAdminBulkRecipeIngredient[] = [];

  for (let i = 1; i <= 80; i++) {
    const name = cell(raw, `Ingredients ${i} Display Name`, `Ingredients${i}DisplayName`);
    if (!name) continue;

    const quantity =
      parseQuantityToken(cell(raw, `Ingredients ${i} Quantity`, `Ingredients${i}Quantity`)) || 0;
    const approximateValue =
      parseQuantityToken(
        cell(raw, `Ingredients ${i} Approximate Value`, `Ingredients${i}ApproximateValue`)
      ) || quantity;
    const unitLabel = cell(raw, `Ingredients ${i} Unit Label`, `Ingredients${i}UnitLabel`);
    const ingredientMasterId =
      Number(
        cell(
          raw,
          `Ingredients ${i} Ingredient Master ID`,
          `Ingredients ${i} Ingredient Ref ID`,
          `Ingredients${i}IngredientMasterId`
        )
      ) || 0;
    const servingUnitMasterId =
      Number(
        cell(raw, `Ingredients ${i} Serving Unit Master ID`, `Ingredients${i}ServingUnitMasterId`)
      ) || 0;

    const unitNorm = String(unitLabel || '')
      .trim()
      .toLowerCase()
      .replace(/\./g, '');
    const gramLike =
      !unitNorm ||
      unitNorm === 'g' ||
      unitNorm === 'gm' ||
      unitNorm === 'gram' ||
      unitNorm === 'grams' ||
      unitNorm === 'kg' ||
      unitNorm === 'ml';
    // Approximate Value is often "1" for 1 cup — that is NOT grams.
    const approxAsGrams = gramLike
      ? approximateValue
      : approximateValue > 0 && quantity > 0 && approximateValue > quantity * 5
        ? approximateValue
        : 0;

    drafts.push({
      name,
      quantity,
      approximateGrams: approxAsGrams,
      unitLabel: unitLabel || undefined,
    });

    if (ingredientMasterId > 0 || servingUnitMasterId > 0 || name) {
      recipeIngredients.push({
        ingredientId: ingredientMasterId,
        ingredientName: name,
        servingUnitMasterId,
        unitLabel: unitLabel || '',
        approximateValue: gramLike ? approximateValue : approxAsGrams || approximateValue,
        quantity,
        totalGrams: approxAsGrams,
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        fiber: 0,
      });
    }
  }

  return { drafts, recipeIngredients };
}

function parseProfessionalNotesFromRow(raw: Record<string, string>): string {
  const parts: string[] = [];
  for (let i = 1; i <= 30; i++) {
    const title = cell(
      raw,
      `Professional Notes ${i} Title`,
      `ProfessionalNotes${i}Title`,
      `Professional Notes${i} Title`
    );
    const body = cell(
      raw,
      `Professional Notes ${i} Body`,
      `ProfessionalNotes${i}Body`,
      `Professional Notes${i} Body`
    );
    if (!title && !body) continue;
    parts.push(`${title}|${body}`);
  }
  if (parts.length) return parts.join(';;');
  // Single free-text column (237-field export often has this alone).
  return cell(raw, 'ProfessionalNotes', 'Professional Notes', 'Professional Note');
}

function parseLocalNamesFromCells(
  raw: Record<string, string>
): Array<{ localName: string; languageCode: string | null; stateCode: string | null; title: string | null }> {
  const items: Array<{
    localName: string;
    languageCode: string | null;
    stateCode: string | null;
    title: string | null;
  }> = [];
  for (let i = 1; i <= 30; i++) {
    const localName = cell(raw, `Local Names ${i} Local Name`, `LocalNames${i}LocalName`);
    if (!localName) continue;
    items.push({
      localName,
      languageCode: cell(raw, `Local Names ${i} Language Code`, `LocalNames${i}LanguageCode`) || null,
      stateCode: cell(raw, `Local Names ${i} State Code`, `LocalNames${i}StateCode`) || null,
      title: cell(raw, `Local Names ${i} Title`, `LocalNames${i}Title`) || null,
    });
  }
  return items;
}

function parseIdList(...values: string[]): number[] {
  const ids = new Set<number>();
  for (const value of values) {
    String(value || '')
      .split(/[,;|]/)
      .map((s) => Number(String(s).trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .forEach((n) => ids.add(n));
  }
  return [...ids];
}

/** Normalize labels for fuzzy master matching (Veg ↔ Vegetarian, etc.). */
function normalizeLookupNeedle(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_/\\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\bnon\s+veg(?:etarian)?\b/g, 'nonveg')
    .replace(/\bvegetarian\b/g, 'veg')
    .replace(/\beggetarian\b/g, 'egg')
    .replace(/\bnonveg\b/g, 'non veg')
    .trim();
}

const LOOKUP_NAME_SYNONYMS: Record<string, string[]> = {
  veg: ['veg', 'vegetarian', 'pure veg', 'pureveg'],
  'non veg': ['non veg', 'nonveg', 'non-veg', 'non vegetarian', 'nonvegetarian'],
  egg: ['egg', 'eggetarian', 'eggitarian'],
  eat: ['eat', 'green', '1'],
  ctrl: ['ctrl', 'control', 'yellow', '2'],
  alt: ['alt', 'alternate', 'orange', '3'],
  del: ['del', 'delete', 'red', '4'],
};

function expandLookupNeedles(raw: string): string[] {
  const base = normalizeLookupNeedle(raw);
  if (!base) return [];
  const out = new Set<string>([base, String(raw || '').trim().toLowerCase()]);
  for (const [canon, alts] of Object.entries(LOOKUP_NAME_SYNONYMS)) {
    if (alts.includes(base) || base === canon) {
      alts.forEach((a) => out.add(a));
      out.add(canon);
    }
  }
  return [...out].filter(Boolean);
}

function findLookupByNameFlexible(
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  name: string
): { id: string | number; name: string } | null {
  if (!name?.trim() || !options?.length) return null;

  // Prefer existing exact/prefix/contains matcher first.
  const direct = findLookupByName(options as any, name);
  if (direct) return direct;

  const needles = expandLookupNeedles(name);
  for (const opt of options) {
    const candidates = [opt.name, opt.label]
      .map((v) => normalizeLookupNeedle(String(v || '')))
      .filter(Boolean);
    if (candidates.some((c) => needles.includes(c))) return opt;
  }

  // Soft contains with synonyms (length guard).
  for (const needle of needles) {
    if (needle.length < 3) continue;
    const hit = options.find((o) => {
      const n = normalizeLookupNeedle(String(o.name || o.label || ''));
      return n.includes(needle) || needle.includes(n);
    });
    if (hit) return hit;
  }
  return null;
}

function matchLookupIdsByNames(
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  namesRaw: string
): number[] {
  return resolveMasterTokensToNumericIds(options, namesRaw);
}

function splitMasterTokens(raw: string): string[] {
  return String(raw || '')
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isAllSeasonsToken(token: string): boolean {
  const n = normalizeLookupNeedle(token);
  return (
    n === 'all' ||
    n === 'all season' ||
    n === 'all seasons' ||
    n === 'allseason' ||
    n === 'allseasons' ||
    n === 'year round' ||
    n === 'yearround'
  );
}

/**
 * Resolve file tokens that may be master IDs, master names, or a mix.
 * Only values that exist in master are kept (when lookups are loaded).
 */
function resolveMasterTokensToNumericIds(
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  raw: string
): number[] {
  const tokens = splitMasterTokens(raw);
  if (!tokens.length) return [];

  const ids: number[] = [];
  for (const token of tokens) {
    const byId = options?.length ? findLookupById(options as any, token) : null;
    if (byId) {
      const id = Number(byId.id);
      if (Number.isFinite(id) && id > 0) ids.push(id);
      continue;
    }

    // Pure numeric ID while lookups not loaded yet — keep for later validation.
    if (/^\d+$/.test(token) && (!options || !options.length)) {
      const id = Number(token);
      if (id > 0) ids.push(id);
      continue;
    }

    // Pure numeric but not found in master → reject
    if (/^\d+$/.test(token)) continue;

    const byName = findLookupByNameFlexible(options, token);
    const id = Number(byName?.id);
    if (Number.isFinite(id) && id > 0) ids.push(id);
  }
  return [...new Set(ids)];
}

function resolveMasterTokenToStringId(
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  raw: string
): string | null {
  const tokens = splitMasterTokens(raw);
  if (!tokens.length || !options?.length) return null;
  for (const token of tokens) {
    const byId = findLookupById(options as any, token);
    if (byId) return String(byId.id);
    const byName = findLookupByNameFlexible(options, token);
    if (byName) return String(byName.id);
  }
  return null;
}

/**
 * Read ID-column and name-column cells together.
 * Each token may be a master ID or a master name.
 * Only values present in master lookups are accepted.
 */
function hydrateMultiFromFile(
  raw: Record<string, string>,
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  idAliases: string[],
  nameAliases: string[]
): number[] {
  const combined = [cell(raw, ...idAliases), cell(raw, ...nameAliases)]
    .filter((v) => !!String(v || '').trim())
    .join(',');
  return resolveMasterTokensToNumericIds(options, combined);
}

function hydrateSingleFromFile(
  raw: Record<string, string>,
  options: Array<{ id: string | number; name: string; label?: string }> | undefined,
  idAliases: string[],
  nameAliases: string[]
): number | null {
  const ids = hydrateMultiFromFile(raw, options, idAliases, nameAliases);
  return ids.length ? ids[0] : null;
}

function firstMatchedStatus(rawStatus: string): string | null {
  const needle = String(rawStatus || '').trim().toLowerCase();
  if (!needle) return null;
  return (
    SUPER_ADMIN_RECIPE_STATUS_OPTIONS.find((s) => s.toLowerCase() === needle) || null
  );
}

function parseRowText(raw: Record<string, string>): SuperAdminBulkParsedText {
  return {
    recipeName: cell(raw, 'RecipeName', 'Recipe Name', 'Name', 'Original Name', 'OriginalName'),
    originalName: cell(raw, 'OriginalName', 'Original Name', 'Alias'),
    alternateName: cell(raw, 'AlternateName', 'Alternate Name'),
    numberOfServes: cell(raw, 'NumberOfServes', 'Number Of Serves', 'Serves'),
    referenceUrl: cell(raw, 'ReferenceUrl', 'Reference URL'),
    recipeDescription: cell(raw, 'RecipeDescription', 'Recipe Description', 'Description'),
    prepTime: cell(raw, 'PrepTime', 'Prep Time', 'Prep Time Minutes', 'PrepTimeMinutes'),
    soakingTime: cell(
      raw,
      'SoakingTime',
      'Soaking',
      'Soaking Time Minutes',
      'SoakingTimeMinutes'
    ),
    fermentingTime: cell(
      raw,
      'FermetingTime',
      'FermentingTime',
      'Fermenting',
      'Fermenting Time Minutes',
      'FermentingTimeMinutes'
    ),
    bakingTime: cell(raw, 'BakingTime', 'Baking', 'Baking Time Minutes', 'BakingTimeMinutes'),
    cookTime: cell(raw, 'CookTime', 'Cook Time', 'Cook Time Minutes', 'CookTimeMinutes'),
    professionalNotes: parseProfessionalNotesFromRow(raw),
    chefAuthorNotes: cell(raw, 'ChefAuthorNotes', 'Chef Author Notes'),
    chefAuthorTips: cell(raw, 'ChefAuthorTips', 'Chef Author Tips'),
    chefMethodProcesses: cell(raw, 'ChefMethodProcesses', 'Chef Method Processes'),
    remark: cell(raw, 'Remark', 'Remarks', 'Recipe Remark', 'Recipe Remarks'),
    ingredientsRaw: cell(raw, 'Ingredients'),
    shelfLife: cell(
      raw,
      'ShelfLife',
      'Shelf Life',
      'Storage Reheating Shelf Life',
      'StorageReheatingShelfLife'
    ),
    storageInstructions: cell(
      raw,
      'StorageInstructions',
      'Storage Instructions',
      'Storage Reheating Storage Instructions',
      'StorageReheatingStorageInstructions'
    ),
    reheatTemperature: cell(
      raw,
      'ReheatTemperature',
      'Reheat Temperature',
      'Storage Reheating Reheat Temperature',
      'StorageReheatingReheatTemperature'
    ),
    reheatingInstructions: cell(
      raw,
      'ReheatingInstructions',
      'Reheating Instructions',
      'Storage Reheating Reheating Instructions',
      'StorageReheatingReheatingInstructions'
    ),
    ccpNotes: cell(
      raw,
      'CcpNotes',
      'CCP',
      'CCP Notes',
      'Critical Control Points',
      'Storage Reheating Ccp Notes',
      'StorageReheatingCcpNotes'
    ),
    costingNotes: cell(raw, 'CostingNotes', 'Costing Notes'),
    videoReferenceUrl: cell(raw, 'VideoReferenceUrl', 'Video Reference URL'),
  };
}

function buildDisplay(parsed: SuperAdminBulkParsedText): Record<string, string> {
  return {
    RecipeName: parsed.recipeName,
    NumberOfServes: parsed.numberOfServes,
    RecipeDescription: parsed.recipeDescription,
    OriginalName: parsed.originalName,
    AlternateName: parsed.alternateName,
  };
}

function getOptionNamesByIds(
  options: Array<{ id: string | number; name: string }> | undefined,
  ids: number[]
): string | null {
  if (!ids?.length || !options?.length) return null;
  const names = ids
    .map((id) => options.find((o) => Number(o.id) === Number(id))?.name)
    .filter(Boolean);
  return names.length ? names.join(', ') : null;
}

function toApiLongIds(id: string | number | null | undefined): number[] {
  if (id == null || id === '') return [];
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

function getIsVegFromDietaryName(name: string | null): boolean | null {
  const normalized = (name || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('non') && normalized.includes('veg')) return false;
  if (normalized === 'vegan' || normalized === 'vegetarian' || normalized === 'veg') return true;
  if (normalized.includes('egg') || normalized.includes('eggetarian')) return null;
  if (normalized.includes('veg')) return true;
  return null;
}

function getSeasonIdsForApi(
  seasonIds: number[],
  seasonOptions: Array<{ id: string | number; name: string }>
): number[] {
  const selectedIds = seasonIds.filter((id) => id > 0);
  const allSeasonsSelected = seasonOptions.some((option) => {
    const normalized = String(option.name || '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
    return normalized === 'all season' && selectedIds.includes(Number(option.id));
  });
  if (allSeasonsSelected) {
    return seasonOptions.map((o) => Number(o.id)).filter((id) => Number.isFinite(id) && id > 0);
  }
  return selectedIds;
}

function getCuisineSubRegionIdsForApi(
  selections: SuperAdminBulkRowSelections,
  allRegionWiseSubRegions: Array<{ id: number; region?: string }>
): number[] {
  const explicit = (selections.cuisineSubRegionIds || []).filter((id) => id > 0);
  if (explicit.length > 0) {
    return [...new Set(explicit)];
  }
  const labels = new Set(
    (selections.cuisineRegionLabels || []).map((l) => l.trim().toLowerCase()).filter(Boolean)
  );
  if (!labels.size || !allRegionWiseSubRegions.length) return [];
  return [
    ...new Set(
      allRegionWiseSubRegions
        .filter((r) => labels.has((r.region || '').trim().toLowerCase()))
        .map((r) => Number(r.id))
        .filter((id) => id > 0)
    ),
  ];
}

function validateRequiredFields(
  parsed: SuperAdminBulkParsedText,
  selections: SuperAdminBulkRowSelections
): string[] {
  return getSuperAdminBulkFieldErrors(parsed, selections).map((e) => e.message);
}

export interface SuperAdminBulkFieldError {
  field: string;
  message: string;
  section: number;
}

/** Field-level validation for bulk upload — no master/text fields are required. */
export function getSuperAdminBulkFieldErrors(
  parsed: SuperAdminBulkParsedText,
  selections: SuperAdminBulkRowSelections
): SuperAdminBulkFieldError[] {
  const errors: SuperAdminBulkFieldError[] = [];
  // Intentionally no required-field checks (Name / Serves / Description / dropdowns).
  // Assignment status still needs emails when that workflow status is selected.
  if (selections.status === 'Assigned to Maker') {
    const a = selections.assignment;
    if (!a?.makerEmail || !a?.checkerEmail) {
      errors.push({
        section: 12,
        field: 'assignment',
        message: 'Assign Maker & Checker when status is "Assigned to Maker"',
      });
    }
  } else if (selections.status === 'Assigned to Checker') {
    if (!selections.assignment?.checkerEmail) {
      errors.push({
        section: 12,
        field: 'assignment',
        message: 'Assign Checker when status is "Assigned to Checker"',
      });
    }
  }

  return errors;
}

function parseGeneratedFssaiTagsFromCells(raw: Record<string, string>): SuperAdminBulkGeneratedFssaiTag[] {
  const tags: SuperAdminBulkGeneratedFssaiTag[] = [];
  for (let i = 1; i <= 20; i++) {
    const masterId = Number(
      cell(
        raw,
        `Generated FSSAI Nutrient Tags ${i} FSSAI Nutrient Tag Master ID`,
        `GeneratedFssaiNutrientTags${i}FssaiNutrientTagMasterId`
      )
    );
    const tagName = cell(
      raw,
      `Generated FSSAI Nutrient Tags ${i} FSSAI Tag Name`,
      `GeneratedFssaiNutrientTags${i}FssaiTagName`
    );
    if (!masterId && !tagName) continue;
    tags.push({
      fssaiNutrientTagMasterId: masterId || 0,
      fssaiTagName: tagName || undefined,
      operator:
        cell(raw, `Generated FSSAI Nutrient Tags ${i} Operator`) || undefined,
      minValue: (() => {
        const n = Number(cell(raw, `Generated FSSAI Nutrient Tags ${i} Min Value`));
        return Number.isFinite(n) ? n : undefined;
      })(),
      maxValue: (() => {
        const n = Number(cell(raw, `Generated FSSAI Nutrient Tags ${i} Max Value`));
        return Number.isFinite(n) ? n : undefined;
      })(),
      nutrientName:
        cell(raw, `Generated FSSAI Nutrient Tags ${i} Nutrient Name`) || undefined,
      stateOfFood:
        cell(raw, `Generated FSSAI Nutrient Tags ${i} State Of Food`) || undefined,
    });
  }
  return tags.filter((t) => t.fssaiNutrientTagMasterId > 0 || !!t.fssaiTagName);
}

export function rebuildSuperAdminRecipePayload(
  row: SuperAdminBulkPreviewRow,
  lookups: SuperAdminBulkLookups,
  allRegionWiseSubRegions: Array<{ id: number; region?: string }> = []
): void {
  const { parsed, selections } = row;
  const errors = validateRequiredFields(parsed, selections);

  row.configured = true;
  row.valid = errors.length === 0;
  row.error = row.valid ? undefined : errors.join('; ');

  if (!row.valid) {
    row.payload = undefined;
    return;
  }

  const sourceMatch = findLookupById(lookups.sources, selections.sourceId);
  const dietaryMatch = findLookupById(lookups.dietaryPreferences, selections.dietaryPreferenceId);
  const dietaryName =
    dietaryMatch?.name ||
    cell(row.cells || {}, 'Dietary Preference', 'DietaryPreference') ||
    null;
  const sourceName =
    sourceMatch?.name || cell(row.cells || {}, 'Source', 'Link Source') || null;
  const serves = Number(parsed.numberOfServes);
  const foodGroupIds = selections.foodGroupIds || [];
  const faoFoodGroupIds = selections.faoFoodGroupIds || [];

  const ingredients = (selections.recipeIngredients || [])
    .filter((i) => String(i.ingredientName || '').trim())
    .map((i) => {
      const masterId = Number(i.ingredientId) || 0;
      const refId = Number(i.ingredientRefId) || 0;
      const totalG = Number(i.totalGrams) || 0;
      const qty = Number(i.quantity) || 0;
      const approx = Number(i.approximateValue) || 0;
      const hasUnit = Number(i.servingUnitMasterId) > 0 && approx > 0;
      // Prefer qty × grams-per-unit when unit known; else send absolute grams as approx with qty=1.
      let sendQty = qty;
      let sendApprox = approx;
      if (totalG > 0) {
        if (hasUnit && qty > 0 && Math.abs(qty * approx - totalG) / Math.max(totalG, 1) <= 0.2) {
          sendQty = qty;
          sendApprox = approx;
        } else if (hasUnit && approx > 0) {
          sendQty = qty > 0 ? qty : totalG / approx;
          sendApprox = approx;
        } else {
          sendQty = 1;
          sendApprox = totalG;
        }
      } else if (!(sendApprox > 0) && qty > 0) {
        sendApprox = qty;
        sendQty = 1;
      }
      return {
        // Prefer real IngredientRef; else send master id — API maps master → ref before insert.
        ingredientRefId: refId > 0 ? refId : masterId > 0 ? masterId : null,
        nestedRecipeId: null,
        isNestedRecipe: false,
        quantity: sendQty,
        approximateValue: sendApprox,
        servingUnitMasterId: i.servingUnitMasterId > 0 ? i.servingUnitMasterId : null,
        unitLabel: i.unitLabel || null,
        displayName: i.ingredientName,
        remark: null,
      };
    });

  // Fallback if recipeIngredients empty but drafts exist.
  if (!ingredients.length && selections.ingredients?.length) {
    selections.ingredients.forEach((d) => {
      if (!d.name) return;
      ingredients.push({
        ingredientRefId: null,
        nestedRecipeId: null,
        isNestedRecipe: false,
        quantity: d.quantity || 0,
        approximateValue: d.approximateGrams || d.quantity || 0,
        servingUnitMasterId: null,
        unitLabel: d.unitLabel || null,
        displayName: d.name,
        remark: null,
      });
    });
  }

  const totalGrams =
    (selections.recipeIngredients || []).reduce((sum, i) => sum + (Number(i.totalGrams) || 0), 0) ||
    (selections.ingredients || []).reduce((sum, i) => sum + (Number(i.approximateGrams) || 0), 0);

  const isAssignmentStatus =
    selections.status === 'Assigned to Maker' || selections.status === 'Assigned to Checker';

  const isVegFromFile = (() => {
    const raw = cell(row.cells || {}, 'Is Veg', 'IsVeg').toLowerCase();
    if (raw === 'yes' || raw === 'true' || raw === '1') return true;
    if (raw === 'no' || raw === 'false' || raw === '0') return false;
    return null;
  })();

  const recipeName =
    (parsed.recipeName || '').trim() ||
    (parsed.originalName || '').trim() ||
    `Untitled Recipe (Row ${row.rowNumber})`;

  row.payload = {
    name: recipeName,
    originalName: parsed.originalName || null,
    alternateName: parsed.alternateName || null,
    description: parsed.recipeDescription || null,
    isVeg: getIsVegFromDietaryName(dietaryName) ?? isVegFromFile,
    isCustom: false,
    // Create as interim status; assignment API sets Assigned to Maker/Checker after import.
    status: isAssignmentStatus ? 'To be assigned' : selections.status || 'Draft',
    prepTimeMinutes: parseTimeToMinutes(parsed.prepTime),
    cookTimeMinutes: parseTimeToMinutes(parsed.cookTime),
    soakingTimeMinutes: parseTimeToMinutes(parsed.soakingTime),
    fermentingTimeMinutes: parseTimeToMinutes(parsed.fermentingTime),
    bakingTimeMinutes: parseTimeToMinutes(parsed.bakingTime),
    thumbnailImagePath: selections.thumbnailImagePath || null,
    mainImagePath: selections.thumbnailImagePath || null,
    serves: Number.isFinite(serves) && serves > 0 ? serves : null,
    referenceUrl: parsed.referenceUrl || null,
    source: sourceName,
    servingWeight: totalGrams > 0 ? Number(totalGrams.toFixed(2)) : null,
    flavour: cell(row.cells || {}, 'Flavour') || null,
    videoReferenceUrl: parsed.videoReferenceUrl || null,
    costingNotes: parsed.costingNotes || parsed.remark || null,
    // Keep Excel remark visible on create when API has no dedicated recipe Remark field.
    remark: parsed.remark || null,
    ingredients,
    media: selections.thumbnailImagePath
      ? [
          {
            mediaType: 'Image',
            filePath: selections.thumbnailImagePath,
            caption: null,
            displayOrder: 0,
          },
        ]
      : [],
    localNames: parseLocalNamesFromCells(row.cells || {}),
    cuisineIds: parseIdList(
      cell(row.cells || {}, 'Cuisine Ids', 'CuisineIds', 'Cuisine Id', 'CuisineId')
    ),
    cuisineContinentIds: selections.cuisineContinentIds || [],
    cuisineRegionIds: parseIdList(cell(row.cells || {}, 'Cuisine Region Ids', 'CuisineRegionIds')),
    cuisineSubRegionIds: getCuisineSubRegionIdsForApi(selections, allRegionWiseSubRegions),
    cuisineSubSubRegionIds: selections.cuisineGeoTagIds || [],
    mealTypeIds: selections.mealTypeIds || [],
    categoryIds: selections.recipeCategoryIds || [],
    allergenIds: selections.allergenContainsIds || [],
    allergenFreeIds: selections.allergenFreeIds || [],
    cookingMethodIds: selections.cookingMethodIds || [],
    tasteIds: selections.tasteIds || [],
    flavourIds: selections.flavourIds || [],
    textureIds: selections.textureIds || [],
    aromaIds: selections.aromaIds || [],
    seasonIds: getSeasonIdsForApi(selections.seasonIds || [], lookups.seasons),
    occasionIds: selections.occasionIds || [],
    mealPreferenceIds: selections.mealPreferenceIds || [],
    dietPreferenceIds: selections.dietaryPreferenceId
      ? [Number(selections.dietaryPreferenceId)].filter((id) => id > 0)
      : [],
    intoleranceIds: selections.intoleranceContainsIds || [],
    intoleranceFreeIds: selections.intoleranceFreeIds || [],
    courseIds: selections.courseIds || [],
    dietaryPreference: dietaryName,
    dietaryPreferenceId: selections.dietaryPreferenceId || null,
    foodGroup:
      getOptionNamesByIds(lookups.foodGroups, foodGroupIds) ||
      cell(row.cells || {}, 'Food Group', 'FoodGroup') ||
      null,
    locationIds: selections.locationIds || [],
    healthColorCodeIds: toApiLongIds(selections.healthColorCodeId),
    utensilEquipmentIds: [...(selections.utensilIds || []), ...(selections.equipmentIds || [])],
    diseaseIds: selections.diseaseIds || [],
    foodExchangeIds: foodGroupIds,
    faoFoodGroupIds,
    faoFoodGroup:
      getOptionNamesByIds(lookups.faoFoodGroups, faoFoodGroupIds) ||
      cell(row.cells || {}, 'Fao Food Group', 'FAO Food Group', 'FaoFoodGroup') ||
      null,
    linkSourceIds: selections.sourceId ? [Number(selections.sourceId)].filter((id) => id > 0) : [],
    stateOfProduct: selections.stateOfProduct || 'Solid',
    storageReheating: {
      storageTypeId: selections.storageTypeId || null,
      shelfLife: parsed.shelfLife || null,
      shelfLifeUnit: selections.shelfLifeUnit || null,
      storageInstructions: parsed.storageInstructions || null,
      reheatMethodId: selections.reheatMethodId || null,
      reheatTemperature: parsed.reheatTemperature || null,
      reheatTemperatureUnit: selections.reheatTemperatureUnit || null,
      reheatingInstructions: parsed.reheatingInstructions || null,
      ccpNotes: parsed.ccpNotes || null,
    },
    professionalNotes: parseProfessionalNotes(parsed.professionalNotes),
    chefMethodProcesses: parseChefMethodProcesses(parsed.chefMethodProcesses),
    chefAuthorNotes: parseSemicolonList(parsed.chefAuthorNotes),
    chefAuthorTips: parseSemicolonList(parsed.chefAuthorTips),
    generatedFssaiNutrientTags: selections.generatedFssaiNutrientTags || [],
    ownerPlatform: cell(row.cells || {}, 'Owner Platform', 'OwnerPlatform') || null,
    ownerType: 'Admin',
    ownerId: null,
  };
}

export function refreshSuperAdminParseCounts(result: SuperAdminBulkParseResult): void {
  result.validCount = result.rows.filter((r) => r.valid).length;
  result.errorCount = result.rows.length - result.validCount;
  result.totalCount = result.rows.length;
}

/** First sheet of .xlsx/.xls → same shape as parseCsvText. */
export function parseExcelArrayBuffer(buffer: ArrayBuffer): { headers: string[]; rows: Record<string, string>[] } {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) {
    return { headers: [], rows: [] };
  }

  const sheet = workbook.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number | boolean | null | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });

  if (!aoa?.length) {
    return { headers: [], rows: [] };
  }

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  if (!headers.some((h) => h.length > 0)) {
    return { headers: [], rows: [] };
  }

  const rows = aoa
    .slice(1)
    .filter((values) => (values || []).some((cell) => String(cell ?? '').trim().length > 0))
    .map((values) => {
      const record: Record<string, string> = {};
      headers.forEach((header, i) => {
        if (!header) return;
        record[header] = String(values?.[i] ?? '').trim();
      });
      return record;
    });

  return { headers, rows };
}

export function validateSuperAdminBulkRows(
  headers: string[],
  rows: Record<string, string>[],
): SuperAdminBulkParseResult {
  if (!headers.length) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: 0,
      fileError: 'File is empty or missing headers.',
    };
  }

  const seenNames = new Set<string>();
  const previewRows: SuperAdminBulkPreviewRow[] = [];

  rows.forEach((raw, index) => {
    const parsed = parseRowText(raw);
    const display = buildDisplay(parsed);
    const errors: string[] = [];

    // No required fields — empty Name/Serves/Description are allowed (placeholder name on import).
    const nameKey = (parsed.recipeName || '').trim().toLowerCase();
    if (nameKey && seenNames.has(nameKey)) {
      errors.push(`Duplicate RecipeName "${parsed.recipeName}" in this file`);
    } else if (nameKey) {
      seenNames.add(nameKey);
    }

    const selections = createDefaultSuperAdminBulkSelections();
    const resolved = resolveIngredientsFromFileRow(raw, parsed.ingredientsRaw);
    selections.ingredients = resolved.drafts;
    selections.recipeIngredients = resolved.recipeIngredients;

    const row: SuperAdminBulkPreviewRow = {
      rowNumber: index + 1,
      valid: errors.length === 0,
      configured: false,
      error: errors.length ? errors.join('; ') : undefined,
      cells: raw,
      display,
      parsed,
      selections,
    };

    previewRows.push(row);
  });

  return {
    rows: previewRows,
    validCount: previewRows.filter((r) => r.valid).length,
    errorCount: previewRows.filter((r) => !r.valid).length,
    totalCount: previewRows.length,
  };
}

export function validateSuperAdminBulkCsv(csvText: string): SuperAdminBulkParseResult {
  const { headers, rows } = parseCsvText(csvText);
  return validateSuperAdminBulkRows(headers, rows);
}

export function validateSuperAdminBulkExcel(buffer: ArrayBuffer): SuperAdminBulkParseResult {
  const { headers, rows } = parseExcelArrayBuffer(buffer);
  return validateSuperAdminBulkRows(headers, rows);
}

/**
 * Auto-fill dropdown selections from uploaded sheet columns:
 * - master ID columns (Meal Type Ids, Link Source Ids, …)
 * - name columns when they match loaded master data (Source, Dietary Preference, Food Group, …)
 * Unknown statuses (e.g. "Research Completed") stay Draft.
 * Only values present in master lookups are accepted.
 */
export function hydrateSuperAdminBulkSelectionsFromFile(
  row: SuperAdminBulkPreviewRow,
  lookups: SuperAdminBulkLookups
): void {
  const raw = row.cells || {};
  const sel = row.selections;

  if (!sel.healthColorCodeId) {
    const healthRaw = [
      cell(raw, 'Health Color Code Ids', 'HealthColorCodeIds'),
      cell(raw, 'Health Color Code', 'HealthColorCode', 'Health Color', 'Color Code'),
    ]
      .filter(Boolean)
      .join(',');
    const healthId = resolveMasterTokenToStringId(lookups.healthColorCodes, healthRaw);
    if (healthId) sel.healthColorCodeId = healthId;
  }

  if (!sel.dietaryPreferenceId) {
    const dietId = hydrateSingleFromFile(
      raw,
      lookups.dietaryPreferences,
      ['Diet Preference Ids', 'DietPreferenceIds', 'Dietary Preference Ids'],
      ['Dietary Preference', 'DietaryPreference', 'Diet Preference']
    );
    if (dietId) {
      sel.dietaryPreferenceId = dietId;
    } else {
      const isVeg = cell(raw, 'Is Veg', 'IsVeg').toLowerCase();
      if (isVeg === 'yes' || isVeg === 'true' || isVeg === '1') {
        const veg = findLookupByNameFlexible(lookups.dietaryPreferences, 'Veg');
        if (veg) sel.dietaryPreferenceId = Number(veg.id);
      } else if (isVeg === 'no' || isVeg === 'false' || isVeg === '0') {
        const nonVeg = findLookupByNameFlexible(lookups.dietaryPreferences, 'Non Veg');
        if (nonVeg) sel.dietaryPreferenceId = Number(nonVeg.id);
      }
    }
  }

  // Always prefer explicit file dietary text when present (Vegetarian → Veg synonym).
  {
    const dietFromName = findLookupByNameFlexible(
      lookups.dietaryPreferences,
      cell(raw, 'Dietary Preference', 'DietaryPreference', 'Diet Preference')
    );
    if (dietFromName) sel.dietaryPreferenceId = Number(dietFromName.id);
  }

  const mealTypeIds = hydrateMultiFromFile(
    raw,
    lookups.mealTypes,
    ['Meal Type Ids', 'MealTypeIds'],
    ['Meal Type', 'Meal Types', 'MealType']
  );
  if (mealTypeIds.length) sel.mealTypeIds = mealTypeIds;

  const sourceId = hydrateSingleFromFile(
    raw,
    lookups.sources,
    ['Link Source Ids', 'LinkSourceIds', 'Source Ids', 'SourceIds'],
    ['Source', 'Link Source', 'Recipe Source']
  );
  if (sourceId) sel.sourceId = sourceId;

  const categoryIds = hydrateMultiFromFile(
    raw,
    lookups.recipeCategories,
    ['Category Ids', 'CategoryIds', 'Recipe Category Ids'],
    ['Category', 'Recipe Category', 'Categories']
  );
  if (categoryIds.length) sel.recipeCategoryIds = categoryIds;

  const mealPreferenceIds = hydrateMultiFromFile(
    raw,
    lookups.mealPreferences,
    ['Meal Preference Ids', 'MealPreferenceIds'],
    ['Meal Preference', 'Meal Preferences', 'MealPreference']
  );
  if (mealPreferenceIds.length) sel.mealPreferenceIds = mealPreferenceIds;

  const foodGroupIds = hydrateMultiFromFile(
    raw,
    lookups.foodGroups,
    ['Food Exchange Ids', 'FoodExchangeIds', 'Food Group Ids', 'FoodGroupIds'],
    ['Food Group', 'FoodGroup', 'Food Groups']
  );
  if (foodGroupIds.length) sel.foodGroupIds = foodGroupIds;

  const faoIds = hydrateMultiFromFile(
    raw,
    lookups.faoFoodGroups,
    ['Fao Food Group Ids', 'FaoFoodGroupIds', 'FAO Food Group Ids'],
    ['Fao Food Group', 'FAO Food Group', 'FaoFoodGroup', 'FAO Food Groups']
  );
  if (faoIds.length) sel.faoFoodGroupIds = faoIds;

  // ── 2. Cooking ──────────────────────────────────────────────────────
  const cookingMethodIds = hydrateMultiFromFile(
    raw,
    lookups.cookingMethods,
    ['Cooking Method Ids', 'CookingMethodIds'],
    ['Cooking Method', 'Cooking Methods', 'Cooking Process']
  );
  if (cookingMethodIds.length) sel.cookingMethodIds = cookingMethodIds;

  {
    const utensilEquipRaw = [
      cell(raw, 'Utensil Equipment Ids', 'UtensilEquipmentIds'),
      cell(raw, 'Utensils', 'Utensil', 'Utensil Ids', 'UtensilIds'),
      cell(raw, 'Equipment', 'Equipments', 'Equipment Ids', 'EquipmentIds'),
    ]
      .filter(Boolean)
      .join(',');

    if (utensilEquipRaw.trim()) {
      const utensilIds = resolveMasterTokensToNumericIds(lookups.utensils, utensilEquipRaw);
      const equipmentIds = resolveMasterTokensToNumericIds(lookups.equipment, utensilEquipRaw);
      // Prefer split by master membership; fall back to dedicated columns only.
      if (utensilIds.length) sel.utensilIds = utensilIds;
      if (equipmentIds.length) sel.equipmentIds = equipmentIds;

      if (!sel.utensilIds?.length) {
        const onlyUtensils = hydrateMultiFromFile(
          raw,
          lookups.utensils,
          ['Utensil Ids', 'UtensilIds'],
          ['Utensils', 'Utensil']
        );
        if (onlyUtensils.length) sel.utensilIds = onlyUtensils;
      }
      if (!sel.equipmentIds?.length) {
        const onlyEquipment = hydrateMultiFromFile(
          raw,
          lookups.equipment,
          ['Equipment Ids', 'EquipmentIds'],
          ['Equipment', 'Equipments']
        );
        if (onlyEquipment.length) sel.equipmentIds = onlyEquipment;
      }
    }
  }

  // ── 3. Sensory ──────────────────────────────────────────────────────
  const tasteIds = hydrateMultiFromFile(
    raw,
    lookups.tastes,
    ['Taste Ids', 'TasteIds'],
    ['Taste', 'Tastes', 'Taste Profile']
  );
  if (tasteIds.length) sel.tasteIds = tasteIds;

  const textureIds = hydrateMultiFromFile(
    raw,
    lookups.textures,
    ['Texture Ids', 'TextureIds'],
    ['Texture', 'Textures', 'Texture Details']
  );
  if (textureIds.length) sel.textureIds = textureIds;

  const flavourIds = hydrateMultiFromFile(
    raw,
    lookups.flavours,
    ['Flavour Ids', 'FlavourIds', 'Flavor Ids'],
    ['Flavour', 'Flavor', 'Flavour Notes', 'Flavours']
  );
  if (flavourIds.length) sel.flavourIds = flavourIds;

  const aromaIds = hydrateMultiFromFile(
    raw,
    lookups.aromas,
    ['Aroma Ids', 'AromaIds'],
    ['Aroma', 'Aromas', 'Aroma / Scent', 'Scent']
  );
  if (aromaIds.length) sel.aromaIds = aromaIds;

  // ── 4. Geo / Cuisine ────────────────────────────────────────────────
  // Only Cuisine Continent* columns → continent mappings (RecipeCuisineContinents).
  // "Cuisine Ids" are often RecipeCuisine / sub-sub-region IDs — do NOT put them in continentIds.
  const continentIds = hydrateMultiFromFile(
    raw,
    lookups.continents,
    ['Cuisine Continent Ids', 'CuisineContinentIds'],
    ['Cuisine Continent', 'Continent']
  );
  if (continentIds.length) sel.cuisineContinentIds = continentIds;

  const regionLabelsRaw = cell(
    raw,
    'Cuisine Region',
    'Cuisine Regions',
    'Region',
    'Regions',
    'Cuisine Region Labels'
  );
  if (regionLabelsRaw) {
    // Keep free-text labels; also accept numeric Cuisine Region Ids separately below.
    const labelTokens = splitMasterTokens(regionLabelsRaw).filter((t) => !/^\d+$/.test(t));
    if (labelTokens.length) sel.cuisineRegionLabels = labelTokens;
  }

  const cuisineRegionIds = resolveMasterTokensToNumericIds(
    undefined,
    [
      cell(raw, 'Cuisine Region Ids', 'CuisineRegionIds'),
      // Numeric tokens in Cuisine Region name column still flow to payload via rebuild
      cell(raw, 'Cuisine Region', 'Cuisine Regions', 'Region', 'Regions'),
    ]
      .filter(Boolean)
      .join(',')
  );
  // Region IDs are label-driven in UI; stash numeric IDs into cells for rebuild payload.
  if (cuisineRegionIds.length) {
    raw['Cuisine Region Ids'] = cuisineRegionIds.join(',');
  }

  const subRegionIds = resolveMasterTokensToNumericIds(
    undefined,
    [
      cell(raw, 'Cuisine Sub Region Ids', 'CuisineSubRegionIds'),
      cell(raw, 'Cuisine Sub Region', 'Cuisine Sub Regions', 'Sub Region', 'Sub Regions'),
    ]
      .filter(Boolean)
      .join(',')
  );
  if (subRegionIds.length) sel.cuisineSubRegionIds = subRegionIds;

  const geoTagIds = resolveMasterTokensToNumericIds(
    undefined,
    [
      cell(raw, 'Cuisine Sub Sub Region Ids', 'CuisineSubSubRegionIds'),
      cell(
        raw,
        'Cuisine Sub Sub Region',
        'Cuisine Sub Sub Regions',
        'Sub Sub Region',
        'Geo Tag',
        'Geo Tags'
      ),
    ]
      .filter(Boolean)
      .join(',')
  );
  if (geoTagIds.length) sel.cuisineGeoTagIds = geoTagIds;

  const occasionIds = hydrateMultiFromFile(
    raw,
    lookups.occasions,
    ['Occasion Ids', 'OccasionIds'],
    ['Occasion', 'Occasions']
  );
  if (occasionIds.length) sel.occasionIds = occasionIds;

  const locationIds = hydrateMultiFromFile(
    raw,
    lookups.locations,
    ['Location Ids', 'LocationIds'],
    ['Location', 'Locations']
  );
  if (locationIds.length) sel.locationIds = locationIds;

  const seasonIds = hydrateMultiFromFile(
    raw,
    lookups.seasons,
    ['Season Ids', 'SeasonIds'],
    ['Season', 'Seasons']
  );
  const seasonRawCombined = [
    cell(raw, 'Season Ids', 'SeasonIds'),
    cell(raw, 'Season', 'Seasons'),
  ]
    .filter(Boolean)
    .join(',');
  if (splitMasterTokens(seasonRawCombined).some(isAllSeasonsToken) && lookups.seasons?.length) {
    sel.seasonIds = lookups.seasons
      .map((s) => Number(s.id))
      .filter((id) => Number.isFinite(id) && id > 0);
  } else if (seasonIds.length) {
    sel.seasonIds = seasonIds;
  } else {
    // Chapter-1 style Chef Author Notes often embed "Season: All season"
    const authorNotes = cell(raw, 'Chef Author Notes', 'ChefAuthorNotes');
    const seasonEmbed = authorNotes.match(/season\s*:\s*([^;|]+)/i);
    if (seasonEmbed?.[1]) {
      if (isAllSeasonsToken(seasonEmbed[1]) && lookups.seasons?.length) {
        sel.seasonIds = lookups.seasons
          .map((s) => Number(s.id))
          .filter((id) => Number.isFinite(id) && id > 0);
      } else {
        const matched = matchLookupIdsByNames(lookups.seasons, seasonEmbed[1].trim());
        if (matched.length) sel.seasonIds = matched;
      }
    }
  }

  // ── 5. Allergens ────────────────────────────────────────────────────
  const allergenIds = hydrateMultiFromFile(
    raw,
    lookups.allergens,
    ['Allergen Ids', 'AllergenIds'],
    ['Allergen', 'Allergens', 'Allergens Contains']
  );
  if (allergenIds.length) sel.allergenContainsIds = allergenIds;

  const allergenFreeIds = hydrateMultiFromFile(
    raw,
    lookups.allergenFree,
    ['Allergen Free Ids', 'AllergenFreeIds'],
    ['Allergen Free', 'Allergens Free', 'Free From Allergens']
  );
  if (allergenFreeIds.length) sel.allergenFreeIds = allergenFreeIds;

  const intoleranceIds = hydrateMultiFromFile(
    raw,
    lookups.intolerances,
    ['Intolerance Ids', 'IntoleranceIds'],
    ['Intolerance', 'Intolerances']
  );
  if (intoleranceIds.length) sel.intoleranceContainsIds = intoleranceIds;

  const intoleranceFreeIds = hydrateMultiFromFile(
    raw,
    lookups.intoleranceFree,
    ['Intolerance Free Ids', 'IntoleranceFreeIds'],
    ['Intolerance Free', 'Intolerances Free']
  );
  if (intoleranceFreeIds.length) sel.intoleranceFreeIds = intoleranceFreeIds;

  const diseaseIds = hydrateMultiFromFile(
    raw,
    lookups.diseases,
    ['Disease Ids', 'DiseaseIds'],
    ['Disease', 'Diseases']
  );
  if (diseaseIds.length) sel.diseaseIds = diseaseIds;

  const courseIds = hydrateMultiFromFile(
    raw,
    lookups.courses,
    ['Course Ids', 'CourseIds'],
    ['Course', 'Courses']
  );
  if (courseIds.length) sel.courseIds = courseIds;

  // ── 7. Storage ──────────────────────────────────────────────────────
  const storageTypeId = hydrateSingleFromFile(
    raw,
    lookups.storageTypes,
    [
      'Storage Reheating Storage Type ID',
      'StorageReheatingStorageTypeId',
      'StorageTypeId',
      'Storage Type Id',
    ],
    ['Storage Type', 'StorageType', 'Storage']
  );
  if (storageTypeId) sel.storageTypeId = storageTypeId;

  const reheatMethodId = hydrateSingleFromFile(
    raw,
    lookups.reheatMethods,
    [
      'Storage Reheating Reheat Method ID',
      'StorageReheatingReheatMethodId',
      'ReheatMethodId',
      'Reheat Method Id',
    ],
    ['Reheat Method', 'ReheatMethod', 'Reheating Method']
  );
  if (reheatMethodId) sel.reheatMethodId = reheatMethodId;

  const shelfLifeUnit = cell(
    raw,
    'Storage Reheating Shelf Life Unit',
    'StorageReheatingShelfLifeUnit',
    'ShelfLifeUnit',
    'Shelf Life Unit'
  );
  if (shelfLifeUnit) {
    const matched = SUPER_ADMIN_SHELF_LIFE_UNITS.find(
      (u) => u.toLowerCase() === shelfLifeUnit.toLowerCase()
    );
    sel.shelfLifeUnit = matched || shelfLifeUnit;
  }

  const tempUnit = cell(
    raw,
    'Storage Reheating Reheat Temperature Unit',
    'StorageReheatingReheatTemperatureUnit',
    'ReheatTemperatureUnit',
    'Reheat Temperature Unit'
  );
  if (tempUnit) {
    const matched = SUPER_ADMIN_TEMPERATURE_UNITS.find(
      (u) =>
        u.toLowerCase() === tempUnit.toLowerCase() ||
        u.replace('°', '') === tempUnit.replace('°', '')
    );
    sel.reheatTemperatureUnit = matched || tempUnit;
  }

  const stateOfProduct = cell(raw, 'State Of Product', 'StateOfProduct');
  if (stateOfProduct) {
    const normalized = stateOfProduct.trim().toLowerCase();
    if (normalized === 'liquid') sel.stateOfProduct = 'Liquid';
    else if (normalized === 'solid') sel.stateOfProduct = 'Solid';
    else sel.stateOfProduct = stateOfProduct;
  } else if (!sel.stateOfProduct) {
    sel.stateOfProduct = 'Solid';
  }

  // ── 10. Status ──────────────────────────────────────────────────────
  const matchedStatus = firstMatchedStatus(cell(raw, 'Status'));
  if (matchedStatus) {
    sel.status = matchedStatus;
  }

  // ── Media ───────────────────────────────────────────────────────────
  const thumb = cell(
    raw,
    'Thumbnail Image Path',
    'ThumbnailImagePath',
    'Main Image Path',
    'MainImagePath',
    'Media 1 File Path',
    'Media1FilePath'
  );
  if (thumb) sel.thumbnailImagePath = thumb;

  const fssaiTags = parseGeneratedFssaiTagsFromCells(raw);
  if (fssaiTags.length) {
    sel.generatedFssaiNutrientTags = fssaiTags;
  }
}

export function hydrateAllSuperAdminBulkRowsFromFile(
  rows: SuperAdminBulkPreviewRow[],
  lookups: SuperAdminBulkLookups
): void {
  (rows || []).forEach((row) => hydrateSuperAdminBulkSelectionsFromFile(row, lookups));
}

export function mapMetadataToLookups(meta: any, continentMeta: any): SuperAdminBulkLookups {
  const root = meta?.response ?? meta?.data ?? meta ?? {};
  const continentRoot = continentMeta?.response ?? continentMeta?.data ?? continentMeta ?? {};

  const mapHealth = (list: any[]): SuperAdminBulkLookups['healthColorCodes'] =>
    (list || [])
      .map((item) => ({
        id: String(item.id ?? item.Id ?? ''),
        name: String(item.name ?? item.Name ?? item.label ?? item.Label ?? '').trim(),
        label: String(item.label ?? item.Label ?? item.title ?? item.Title ?? '').trim(),
        colorCode: String(item.colorCode ?? item.ColorCode ?? '').trim(),
      }))
      .filter((item) => item.id && item.name);

  /** Normalize numeric master IDs so ng-select bindValue matches saved selections. */
  const mapNumeric = (list: any[]): SuperAdminBulkLookups['mealTypes'] =>
    extractMetadataOptions(list)
      .map((o) => ({
        id: Number(o.id),
        name: o.name,
      }))
      .filter((o) => Number.isFinite(o.id as number) && (o.id as number) > 0 && !!o.name);

  return {
    healthColorCodes: mapHealth(root.healthColorCodes || root.HealthColorCodes),
    dietaryPreferences: mapNumeric(root.dietaryPreferences || root.DietaryPreferences),
    mealTypes: mapNumeric(root.mealTypes || root.MealTypes),
    sources: mapNumeric(root.recipeLinkSources || root.RecipeLinkSources),
    recipeCategories: mapNumeric(root.categories || root.Categories),
    mealPreferences: mapNumeric(root.mealPreferences || root.MealPreferences),
    foodGroups: mapNumeric(root.foodGroups || root.FoodGroups),
    faoFoodGroups: mapNumeric(root.faoFoodGroups || root.FaoFoodGroups),
    cookingMethods: mapNumeric(root.cookingMethods || root.CookingMethods),
    utensils: mapNumeric(root.utensils || root.Utensils),
    equipment: mapNumeric(root.equipment || root.Equipment),
    tastes: mapNumeric(root.tastes || root.Tastes),
    textures: mapNumeric(root.textures || root.Textures),
    flavours: mapNumeric(root.flavours || root.Flavours),
    aromas: mapNumeric(root.aromas || root.Aromas),
    continents: mapNumeric(
      continentRoot.cuisineContinents || continentRoot.CuisineContinents
    ),
    occasions: mapNumeric(root.occasions || root.Occasions),
    locations: mapNumeric(root.locations || root.Locations),
    seasons: mapNumeric(root.seasons || root.Seasons),
    allergens: mapNumeric(root.allergens || root.Allergens),
    allergenFree: mapNumeric(root.allergenFree || root.AllergenFree),
    intolerances: mapNumeric(root.intolerances || root.Intolerances),
    intoleranceFree: mapNumeric(root.intoleranceFree || root.IntoleranceFree),
    diseases: mapNumeric(root.diseases || root.Diseases),
    courses: mapNumeric(root.courses || root.Courses),
    storageTypes: mapNumeric(root.storageTypes || root.StorageTypes),
    reheatMethods: mapNumeric(root.reheatingMethods || root.ReheatingMethods),
  };
}

export function applyGlobalStatusToRows(
  rows: SuperAdminBulkPreviewRow[],
  status: string
): void {
  rows.forEach((row) => {
    row.selections.status = status;
    if (status !== 'Assigned to Maker' && status !== 'Assigned to Checker') {
      row.selections.assignment = null;
    }
  });
}
